import { useEffect, useMemo, useRef, useState } from 'react'

import {
    DASHBOARD_CARDS,
    type DashboardCardDef,
    type DashboardCardKey,
    defaultSpansForSize,
} from '../../../../../common/dashboard-cards.js'
import { t } from '../../../../../common/i18n.js'
import type { Language } from '../../../../../common/types/i18n.js'
import { SolarProvider } from '../../../../components/icon/solar-icons.js'
import { PageHeader } from '../../../../components/page-header/page-header.js'
import { PageShell } from '../../../../components/page-shell/page-shell.js'
import {
    PortalBanner,
    type PortalBannerProps,
} from '../../../../components/portal-banner/portal-banner.js'
import '../../../../components/portal-dropdown/portal-dropdown.css'
import {
    PortalDropdown,
    type PortalDropdownOption,
} from '../../../../components/portal-dropdown/portal-dropdown.js'
import { Spinner } from '../../../../components/spinner/spinner.js'
import { portalGet, portalPut } from '../../utils/api.js'
import type { DashboardFilterScopes, FilterScope } from '../../utils/portal-api.js'
import { useMatchMedia } from '../../utils/use-match-media.js'
import { CardPicker } from './card-picker.js'
import { CARD_COMPONENTS, isCardActive } from './cards/index.js'
import { DashboardGrid } from './dashboard-grid.js'
import './dashboard-view.css'
import { EditModeToolbar } from './edit-mode-toolbar.js'
import {
    buildPayloadBatches,
    buildPayloadUrl,
    isDirty as isLayoutDirty,
    type Layout,
} from './layout-utils.js'

interface LayoutResponse {
    /** Company-mandated cards for the top "black box" (can't add/delete). */
    mandatoryLayout: Layout
    layout: Layout
    available: DashboardCardDef[]
}

interface DashboardViewProps {
    lang: Language
    userName: string
    isAdmin: boolean
    /** Optional callout strip above the cards (e.g. an open Q12 survey). */
    banner?: PortalBannerProps
}

const buildScopeOptions = (
    scopes: DashboardFilterScopes,
    lang: Language,
): PortalDropdownOption[] => {
    const opts: PortalDropdownOption[] = [
        { value: '', label: t.portal.dashboard.filterAllTeams(lang), depth: 0 },
    ]
    for (const co of scopes.companies) {
        opts.push({ value: `company:${co.id}`, label: co.name, depth: 0 })
        const teams = scopes.teams.filter((tm) => tm.company_id === co.id)
        for (const tm of teams) {
            opts.push({ value: `team:${tm.id}`, label: tm.name, depth: 1 })
        }
    }
    // Teams without a company appear in their own group at the end.
    const unaffiliated = scopes.teams.filter((tm) => tm.company_id === null)
    for (const tm of unaffiliated) {
        opts.push({ value: `team:${tm.id}`, label: tm.name, depth: 1 })
    }
    return opts
}

const valueToScope = (val: string): FilterScope | undefined => {
    if (!val) return undefined
    const [type, idStr] = val.split(':')
    const id = Number(idStr)
    if (type === 'company') return { company_id: id }
    if (type === 'team') return { team_id: id }
    return undefined
}

const scopeToValue = (scope: FilterScope | undefined): string => {
    if (!scope) return ''
    if (scope.company_id) return `company:${scope.company_id}`
    if (scope.team_id) return `team:${scope.team_id}`
    return ''
}

export const DashboardView = ({ lang, userName, isAdmin, banner }: DashboardViewProps) => {
    const firstName = userName.split(' ')[0] ?? userName
    const isMobile = useMatchMedia('(max-width: 767px)')

    const [layout, setLayout] = useState<Layout | null>(null)
    const [layoutLoading, setLayoutLoading] = useState(true)
    const [draft, setDraft] = useState<Layout | null>(null)
    // Mandatory ("black box") section — parallel to layout/draft.
    const [mandatoryLayout, setMandatoryLayout] = useState<Layout | null>(null)
    const [mandatoryDraft, setMandatoryDraft] = useState<Layout | null>(null)
    const [available, setAvailable] = useState<DashboardCardDef[]>([])
    const [editMode, setEditMode] = useState(false)
    const [pickerOpen, setPickerOpen] = useState(false)
    const [filterScope, setFilterScope] = useState<FilterScope | undefined>(undefined)
    const [filterScopes, setFilterScopes] = useState<DashboardFilterScopes | null>(null)
    const [payload, setPayload] = useState<Record<string, unknown>>({})
    const [saveError, setSaveError] = useState<string | null>(null)
    const prevScopeRef = useRef(filterScope)

    // 1. Fetch layout on mount.
    useEffect(() => {
        let cancelled = false
        void (async () => {
            const res = await portalGet<LayoutResponse>('/api/portal/dashboard/layout')
            if (cancelled) return
            if (res.ok) {
                setLayout(res.data.layout)
                setMandatoryLayout(res.data.mandatoryLayout)
                setAvailable(res.data.available)
            }
            // Clear the loading gate on both success and failure so a failed
            // fetch falls through to the (empty) grid instead of spinning forever.
            setLayoutLoading(false)
        })()
        return () => {
            cancelled = true
        }
    }, [])

    // 2. Fetch filter scopes on mount.
    useEffect(() => {
        let cancelled = false
        void (async () => {
            const res = await portalGet<DashboardFilterScopes>(
                '/api/portal/dashboard/filter-scopes',
            )
            if (cancelled) return
            if (res.ok) setFilterScopes(res.data)
        })()
        return () => {
            cancelled = true
        }
    }, [])

    // 3. Fetch payload whenever layout or filterScope changes. Both sections
    //    share the fetch so the black-box cards resolve their payloads too.
    //    Cards are fetched in independent parallel batches (fast DB-backed
    //    cards together, each slow external-API card alone) and merged into
    //    the payload as each response lands, so widgets never block each other.
    const visibleLayout = editMode && draft ? draft : layout
    const visibleMandatory = editMode && mandatoryDraft ? mandatoryDraft : mandatoryLayout
    const payloadCards = [...(visibleMandatory ?? []), ...(visibleLayout ?? [])]
    const layoutKey = payloadCards.map((c) => c.key).join(',')
    useEffect(() => {
        if (payloadCards.length === 0) {
            setPayload({})
            return
        }
        if (scopeToValue(filterScope) !== scopeToValue(prevScopeRef.current)) {
            setPayload((prev) => {
                const next = { ...prev }
                for (const c of payloadCards) {
                    if (DASHBOARD_CARDS[c.key as DashboardCardKey]?.scopeAware) {
                        delete next[c.key]
                    }
                }
                return next
            })
            prevScopeRef.current = filterScope
        }
        let cancelled = false
        for (const batch of buildPayloadBatches(payloadCards)) {
            const url = buildPayloadUrl(batch, filterScope)
            void (async () => {
                const res = await portalGet<Record<string, unknown>>(url)
                if (cancelled) return
                if (res.ok) {
                    setPayload((prev) => ({ ...prev, ...res.data }))
                } else {
                    // Only mark this batch's not-yet-loaded keys as errored;
                    // keep prior successful data intact.
                    setPayload((prev) => {
                        const next = { ...prev }
                        for (const c of batch) {
                            if (next[c.key] === undefined) next[c.key] = { error: res.error }
                        }
                        return next
                    })
                }
            })()
        }
        return () => {
            cancelled = true
        }
    }, [layoutKey, filterScope?.company_id, filterScope?.team_id])

    // 4. Warn before unload while there are unsaved changes (either section).
    const isDirty = useMemo(
        () =>
            editMode &&
            (isLayoutDirty(layout, draft) || isLayoutDirty(mandatoryLayout, mandatoryDraft)),
        [editMode, layout, draft, mandatoryLayout, mandatoryDraft],
    )

    useEffect(() => {
        if (!editMode || !isDirty) return
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault()
            e.returnValue = ''
        }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [editMode, isDirty])

    // ── Edit-mode actions ──

    // Defense-in-depth: edit mode is desktop-only. If the viewport flips to
    // mobile while we're editing, drop edit mode (and any picker/drafts) so
    // the user can't end up in an invalid state.
    useEffect(() => {
        if (isMobile && editMode) {
            setEditMode(false)
            setDraft(null)
            setMandatoryDraft(null)
            setPickerOpen(false)
        }
    }, [isMobile, editMode])

    const startEdit = () => {
        if (!layout) return
        if (isMobile) return // Defense-in-depth: never open edit mode on mobile.
        setDraft(layout.map((c) => ({ ...c })))
        setMandatoryDraft((mandatoryLayout ?? []).map((c) => ({ ...c })))
        setSaveError(null)
        setEditMode(true)
    }

    const cancelEdit = () => {
        setDraft(null)
        setMandatoryDraft(null)
        setSaveError(null)
        setEditMode(false)
        setPickerOpen(false)
    }

    const saveEdit = async () => {
        if (!draft) return
        const res = await portalPut<{ ok: true }>('/api/portal/dashboard/layout', {
            mandatory: mandatoryDraft ?? [],
            regular: draft,
        })
        if (!res.ok) {
            setSaveError(res.error)
            return
        }
        setSaveError(null)
        setLayout(draft)
        setMandatoryLayout(mandatoryDraft)
        setDraft(null)
        setMandatoryDraft(null)
        setEditMode(false)
        setPickerOpen(false)
    }

    // Reorder: the grid hands back the full ordered key list; rebuild the draft
    // preserving each card's spans.
    const handleReorder = (keys: string[]) => {
        if (!draft) return
        const byKey = new Map(draft.map((c) => [c.key, c]))
        setDraft(keys.map((k) => byKey.get(k)).filter((c): c is Layout[number] => c !== undefined))
    }

    const handleRemove = (cardKey: string) => {
        if (!draft) return
        setDraft(draft.filter((c) => c.key !== cardKey))
    }

    const handleAdd = (cardKey: string) => {
        if (!draft) return
        const def = available.find((c) => c.key === cardKey)
        if (!def) return
        setDraft([...draft, { key: cardKey, ...defaultSpansForSize(def.size) }])
        setPickerOpen(false)
    }

    const handleResize = (cardKey: string, patch: { colSpan?: number; rowSpan?: number }) => {
        if (!draft) return
        setDraft(draft.map((c) => (c.key === cardKey ? { ...c, ...patch } : c)))
    }

    // Mandatory section: reorder + resize only (no add/remove). Any card not in
    // the reordered (visible/active) set is kept, so an inactive mandatory card
    // is never dropped from the draft.
    const handleMandatoryReorder = (keys: string[]) => {
        if (!mandatoryDraft) return
        const byKey = new Map(mandatoryDraft.map((c) => [c.key, c]))
        const reordered = keys
            .map((k) => byKey.get(k))
            .filter((c): c is Layout[number] => c !== undefined)
        const rest = mandatoryDraft.filter((c) => !keys.includes(c.key))
        setMandatoryDraft([...reordered, ...rest])
    }

    const handleMandatoryResize = (
        cardKey: string,
        patch: { colSpan?: number; rowSpan?: number },
    ) => {
        if (!mandatoryDraft) return
        setMandatoryDraft(mandatoryDraft.map((c) => (c.key === cardKey ? { ...c, ...patch } : c)))
    }

    // Single-card retry: re-fetch only the one card and merge into payload.
    const handleRetryCard = async (cardKey: string) => {
        const url = buildPayloadUrl([{ key: cardKey, colSpan: 1, rowSpan: 1 }], filterScope)
        const res = await portalGet<Record<string, unknown>>(url)
        if (!res.ok) {
            setPayload((prev) => ({ ...prev, [cardKey]: { error: res.error } }))
            return
        }
        if (res.data[cardKey] === undefined) return
        setPayload((prev) => ({ ...prev, [cardKey]: res.data[cardKey] }))
    }

    // ── Render helpers ──

    const renderedLayout = visibleLayout ?? []
    // Mandatory cards appear only when their payload says they're relevant.
    const renderedMandatory = (visibleMandatory ?? []).filter((c) =>
        isCardActive(c.key, payload[c.key]),
    )
    const enabledKeys = renderedLayout.map((c) => c.key)

    const visibleScopeAware = enabledKeys.some(
        (k) => DASHBOARD_CARDS[k as keyof typeof DASHBOARD_CARDS]?.scopeAware,
    )
    const scopeOptions = filterScopes ? buildScopeOptions(filterScopes, lang) : []
    const hasFilterScopes = scopeOptions.length > 1
    // The team/scope filter is an admin power-tool; mobile dashboards are
    // personal-use, so it's hidden < 768px (scope-aware cards fall back to
    // their default org-wide data there).
    const showFilter = isAdmin && visibleScopeAware && hasFilterScopes && !isMobile

    return (
        <SolarProvider value={{ weight: 'BoldDuotone' }}>
            <PageShell>
                <PageHeader
                    title={t.portal.dashboard.greeting(firstName)(lang)}
                    actions={
                        <>
                            {showFilter && !editMode && (
                                <PortalDropdown
                                    options={scopeOptions}
                                    value={scopeToValue(filterScope)}
                                    onChange={(val) => setFilterScope(valueToScope(val))}
                                    alignRight
                                />
                            )}
                            <EditModeToolbar
                                lang={lang}
                                editMode={editMode}
                                isDirty={isDirty}
                                isMobile={isMobile}
                                onStart={startEdit}
                                onSave={saveEdit}
                                onCancel={cancelEdit}
                                saveError={saveError}
                            />
                        </>
                    }
                />

                {banner && <PortalBanner {...banner} />}

                {renderedMandatory.length > 0 && (
                    <section className="portal-dashboard__mandatory">
                        <DashboardGrid
                            lang={lang}
                            isMobile={isMobile}
                            layout={renderedMandatory}
                            payload={payload}
                            registry={DASHBOARD_CARDS}
                            cardComponents={CARD_COMPONENTS}
                            editMode={editMode}
                            onReorder={handleMandatoryReorder}
                            onRemove={handleRemove}
                            onResize={handleMandatoryResize}
                            onRetryCard={handleRetryCard}
                            onOpenPicker={() => setPickerOpen(true)}
                            variant="mandatory"
                        />
                    </section>
                )}

                {layoutLoading ? (
                    <Spinner />
                ) : (
                    <DashboardGrid
                        lang={lang}
                        isMobile={isMobile}
                        layout={renderedLayout}
                        payload={payload}
                        registry={DASHBOARD_CARDS}
                        cardComponents={CARD_COMPONENTS}
                        editMode={editMode}
                        onReorder={handleReorder}
                        onRemove={handleRemove}
                        onResize={handleResize}
                        onRetryCard={handleRetryCard}
                        onOpenPicker={() => setPickerOpen(true)}
                    />
                )}

                <CardPicker
                    lang={lang}
                    open={pickerOpen}
                    available={available}
                    enabledKeys={enabledKeys}
                    onAdd={handleAdd}
                    onClose={() => setPickerOpen(false)}
                />
            </PageShell>
        </SolarProvider>
    )
}
